import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { Offcanvas, Modal, Dropdown } from "react-bootstrap";
import ConfirmModal from "../../components/modals/ConfirmModal";
import DeviceAttributesPanel from "./DeviceAttributesPanel";
import Select from "react-select";
import { MdClose } from "react-icons/md";
import { useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import StarekContext from "../../context/StarekContext";
import { SaveSuccessPanel } from "../../components/Sidebar";
import {
    GET_OPERATIONS_FOR_PLANT,
    GET_DEVICE_CAPABILITIES,
    operationToOption,
    saveDeviceCapabilities,
} from "./deviceCapabilities";
import {
    FALLBACK_DEVICE_FLAGS,
    computeGpioType,
    decodeGpioType,
    computeUartType,
    decodeUartType,
    normalizeRuntimeSignals,
    compareSignalKeys,
    computePhysicalSignalColors,
    manifestDefaultType,
} from "./gpioTypeUtils";
import {
    fetchAvailableDeviceModels,
    FALLBACK_SIGNAL_COLORS,
} from "../deviceProfile/AddDeviceProfileWizard";
import {
    deviceProtocolOptions,
    defaultSignalConfig,
    entryFromFormValues,
    formValuesFromEntry,
    visibleFields,
    deriveAccess,
} from "./bindingSchema";

// Every GPIO port is fundamentally this same 3-axis bitmask
// (Discrete/Proportional, Sense/Actuate, Control/State) regardless of
// whether a given model's manifest happens to declare selectable dropdowns
// for it in the profile-authoring wizard - a "fixed-type" model like
// starek-nwm-sim just ships with one axis combination as its default, it
// doesn't mean the hardware can't be overridden per-device. So this is
// always decoded/composed from the raw bits, never gated on manifest
// declaration.
const GPIO_TYPE_AXES = [
    { name: "discreteOrProportional", options: ["Discrete", "Proportional"] },
    { name: "actuateOrSense", options: ["Sense", "Actuate"] },
    { name: "controlOrState", options: ["Control", "State"] },
];

// UART's peripheral-init parameters - same category as GPIO's axes above
// ("part of initializing a peripheral"), just rendered as plain selects
// instead of toggle badges since each one has more than 2 options. Label
// text mirrors the manifest's own parameter names.
const UART_PARAMS = [
    { name: "baudrate", label: "Baud" },
    { name: "data_bits", label: "Data bits" },
    { name: "parity", label: "Parity" },
    { name: "stop_bits", label: "Stop bits" },
];

// Icon + tooltip per axis option and per boolean toggle - badges show an
// icon instead of text to stay compact; the title attribute carries the
// full explanation on hover since the icon alone can't.
// Same color-per-shape convention as LiveView's own type pills
// (DeviceMonitorControl.jsx's Proportional/Discrete/Sense/Actuate/Status/
// Control chips) - these buttons and those pills describe the exact same
// axis values, so a signal that reads purple in one place has to read
// purple in the other, or "what shape is this port" silently means two
// different things depending which screen you're looking at it from.
const AXIS_ICON_INFO = {
    Discrete: {
        icon: "fa-wave-square",
        help: "Discrete: reads/writes a simple on/off (digital) value. Click to switch to Proportional.",
        colorClass: "tw-bg-green-100 tw-text-green-700 hover:tw-bg-green-200",
    },
    Proportional: {
        icon: "fa-chart-line",
        help: "Proportional: reads/writes a continuously variable (analog) value. Click to switch to Discrete.",
        colorClass: "tw-bg-purple-100 tw-text-purple-700 hover:tw-bg-purple-200",
    },
    Sense: {
        icon: "fa-eye",
        help: "Sense: this port reads an external signal (input). Click to switch to Actuate.",
        colorClass: "tw-bg-blue-100 tw-text-blue-700 hover:tw-bg-blue-200",
    },
    Actuate: {
        icon: "fa-bolt",
        help: "Actuate: this port drives an external signal (output). Click to switch to Sense.",
        colorClass: "tw-bg-orange-100 tw-text-orange-700 hover:tw-bg-orange-200",
    },
    Control: {
        icon: "fa-sliders-h",
        help: "Control. Click to switch to State.",
        colorClass: "tw-bg-red-100 tw-text-red-700 hover:tw-bg-red-200",
    },
    State: {
        icon: "fa-info-circle",
        help: "State. Click to switch to Control.",
        colorClass: "tw-bg-yellow-100 tw-text-yellow-700 hover:tw-bg-yellow-200",
    },
};
const selectMenuPortalStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};

// A native <option>'s inline `style.color` is ignored by most browsers'
// OS-rendered dropdown popups (confirmed - Chrome/macOS shows plain black
// text regardless), so the Source dropdown's color dot needs a real custom
// dropdown instead of a styled native <select> - react-select (already used
// above for the Device Profile picker) renders its own menu in the page,
// where formatOptionLabel's colored <span> actually shows.
const formatSourceOption = (opt) => (
    <div className="tw-flex tw-items-center tw-gap-2">
        <span
            className="tw-w-2.5 tw-h-2.5 tw-rounded-full tw-shrink-0"
            style={{ backgroundColor: `#${opt.color || "000000"}` }}
        />
        <span>
            {opt.label}
            {opt.disabled ? " (already in use)" : ""}
        </span>
    </div>
);

/**
 * What a machine can do only pays off elsewhere: a part's route step offers the machines
 * that list its operation, and a run picks one of those. Neither screen is reachable from
 * here, so the save points at them.
 */
const capabilityNextSteps = (deviceId, linkedCount) => ({
    heading: `Capability updated for ${deviceId}`,
    subheading:
        linkedCount === 0
            ? "It lists no operations now, so no route step can pick it."
            : `It can now be picked for ${linkedCount} operation${linkedCount === 1 ? "" : "s"}.`,
    steps: [
        {
            label: "Use this machine in a part's route",
            why: "A route step only offers machines that list its operation.",
            path: ["Setup", "Parts", "open a part", "Route"],
            to: "/parts",
        },
        {
            label: "Schedule a run on it",
            why: "The run assigns one machine per step and tracks it from there.",
            path: ["Plan", "Job Schedules", "Add"],
            to: "/job-schedules",
        },
    ],
});

// Tab order in the edit sidebar. "operations" is the device's Capability
// list (which operations it can perform); the tab is labelled Operations
// because that is what the panel itself edits and what users look for.
// Endpoints and Signals used to be two tabs; a physical endpoint's own
// operational params (axis/UART mode) and the named signals built on top of
// it are two sides of the same page now, in one tab - flipping a port from
// Sense to Actuate and seeing which signal reads from it no longer means
// switching screens.
const SIDEBAR_TABS = [
    { key: "signals", label: "Signals" },
    { key: "attributes", label: "Attributes" },
    { key: "locations", label: "Installations" },
    { key: "operations", label: "Capability" },
];

// The protocol/table/codec field grid, shared by every config a signal can
// have - the read (only) config, and, when split, the independent write
// config too. Pulled out once so a read grid and a write grid are the same
// component fed different state, not two copies of this ~90-line block.
const SignalConfigFieldsGrid = ({ config, onChange, physicalType, deviceFlags }) => (
    <div className="tw-grid tw-grid-cols-2 tw-gap-2 tw-mb-2">
        {visibleFields(config, physicalType, deviceFlags).map((f) => (
            <div key={f.name}>
                <label className="tw-text-xs tw-text-gray-500 tw-block">
                    {f.label}
                </label>
                {f.type === "select" ? (
                    <select
                        value={config[f.name] ?? f.default}
                        onChange={(e) =>
                            onChange({ ...config, [f.name]: e.target.value })
                        }
                        className="tw-w-full tw-px-2 tw-py-1 tw-border tw-rounded tw-text-sm"
                    >
                        {f.options.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                ) : f.type === "checkbox" ? (
                    <input
                        type="checkbox"
                        checked={!!config[f.name]}
                        onChange={(e) =>
                            onChange({ ...config, [f.name]: e.target.checked })
                        }
                    />
                ) : (
                    <input
                        type={f.type === "number" ? "number" : "text"}
                        value={config[f.name] ?? ""}
                        onChange={(e) =>
                            onChange({
                                ...config,
                                [f.name]:
                                    f.type === "number"
                                        ? e.target.value === ""
                                            ? null
                                            : Number(e.target.value)
                                        : e.target.value,
                            })
                        }
                        className="tw-w-full tw-px-2 tw-py-1 tw-border tw-rounded tw-text-sm"
                    />
                )}
            </div>
        ))}
    </div>
);

// Protocol select (only shown when the endpoint actually offers a choice,
// e.g. /uart between Modbus RTU and raw-frame) + field grid, for one
// direction (read or write) of a signal against the modal's single,
// standalone Source field - read and write always share one physical
// endpoint (a different channel per direction isn't supported yet), only
// protocol/table/address/codec can diverge between them.
const ProtocolAndConfigFields = ({
    endpoint,
    config,
    onConfigChange,
    physicalTypeByAddress,
    uartConflictWarning,
    deviceFlags,
}) => (
    <>
        {deviceProtocolOptions(endpoint).length > 1 && (
            <>
                <select
                    value={config.protocol}
                    onChange={(e) =>
                        onConfigChange(defaultSignalConfig(e.target.value))
                    }
                    className="tw-w-full tw-px-2 tw-py-1 tw-border tw-rounded tw-text-sm tw-mb-2"
                >
                    {deviceProtocolOptions(endpoint).map((p) => (
                        <option key={p.value} value={p.value}>
                            {p.label}
                        </option>
                    ))}
                </select>
                {uartConflictWarning && (
                    <div className="tw-text-xs tw-text-red-500 tw-mb-2">
                        /uart already has a different protocol in use - it can
                        be a Modbus master or a raw-frame listener, never
                        both.
                    </div>
                )}
            </>
        )}
        <SignalConfigFieldsGrid
            config={config}
            onChange={onConfigChange}
            physicalType={physicalTypeByAddress[endpoint]}
            deviceFlags={deviceFlags}
        />
    </>
);

// Thin wrapper so the modal's source-first flow can ask "what's this GPIO
// port's own access" without repeating deriveAccess's {protocol, ...} shape -
// deriveAccess (bindingSchema.js) is the single source of truth for this
// derivation now, shared with the signal list's own access badge.
function directAccessFor(physicalType, deviceFlags) {
    return deriveAccess({ protocol: "direct", physicalType, deviceFlags });
}

function directAccessLabel(access) {
    if (access.read && access.write) return "Read/Write Config";
    return access.write ? "Write Config" : "Read Config";
}

// Standard entity-style edit sidebar: signal configuration, attribute
// editing (DeviceAttributesPanel), device profile assignment, and location
// linking. Live monitoring (the signal graph, channel switcher) lives in
// the LiveView modal instead (see Devices.jsx's liveViewDevice state /
// onShowLiveView) - the grid no longer has an inline expand row for this.
const DeviceEditSidebar = ({
    isOpen,
    onClose,
    device,
    deviceProfiles = [],
    canWrite = false,
    onSaveSignals,
    onAssignProfile,
    onUnassignProfile,
    onSaveAsProfile,
    onSaveLogicalSignal,
    onSaveInterpretations,
    onRebootDevice,
    onShowLiveView,
    initialTab = "signals",
    zones = [],
    onSaveInstallations,
}) => {
    const starekContext = useContext(StarekContext);
    const deviceFlags = starekContext?.types?.device?.flags || FALLBACK_DEVICE_FLAGS;
    const defaultSignalColors =
        starekContext?.types?.device?.defaultSignalColors || FALLBACK_SIGNAL_COLORS;

    const [activeTab, setActiveTab] = useState("signals");
    // Lets a caller (e.g. LiveView's "+ Create interpretation" banner) open
    // the sidebar directly on a specific tab - only applied on the opening
    // transition, so it doesn't fight the user's own tab clicks once open.
    useEffect(() => {
        if (isOpen) setActiveTab(initialTab || "signals");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, initialTab]);

    // --- Operations this device can perform (Capability mapping) ---
    const navigate = useNavigate();
    const [saveOutcome, setSaveOutcome] = useState(null);
    const [selectedOperations, setSelectedOperations] = useState([]);
    const [savingOperations, setSavingOperations] = useState(false);

    const { data: operationsData, loading: loadingOperations } = useQuery(
        GET_OPERATIONS_FOR_PLANT,
        {
            client: starekContext.engineClient,
            skip: !isOpen || !starekContext.lastUsedPlant?.id,
            variables: {
                where: {
                    plant_id: { equals: starekContext.lastUsedPlant?.id },
                    is_active: { equals: true },
                },
            },
        },
    );

    const {
        data: capabilitiesData,
        loading: loadingCapabilities,
        refetch: refetchCapabilities,
    } = useQuery(GET_DEVICE_CAPABILITIES, {
        client: starekContext.engineClient,
        skip: !isOpen || !device?.id,
        variables: { deviceId: device?.id },
        fetchPolicy: "network-only",
    });

    const operationOptions = useMemo(
        () => (operationsData?.operations || []).map(operationToOption),
        [operationsData?.operations],
    );

    const linkedOperationIds = useMemo(
        () =>
            (capabilitiesData?.capabilities || []).map((c) =>
                Number(c.operation_id),
            ),
        [capabilitiesData?.capabilities],
    );

    useEffect(() => {
        setSaveOutcome(null);
    }, [isOpen, device?.id]);

    // Reset the picker to what is actually stored whenever the device or its links change.
    useEffect(() => {
        const linked = (capabilitiesData?.capabilities || []).map((c) => ({
            value: Number(c.operation_id),
            label: c.operation?.name || `Operation ${c.operation_id}`,
        }));
        setSelectedOperations(linked);
    }, [capabilitiesData?.capabilities, device?.id]);

    const handleSaveOperations = async () => {
        if (!device?.id) return;
        setSavingOperations(true);
        try {
            const { added, removed } = await saveDeviceCapabilities(
                starekContext.engineClient,
                device.id,
                selectedOperations.map((o) => o.value),
                linkedOperationIds,
            );
            await refetchCapabilities();
            if (added || removed) {
                starekContext.showSuccess(
                    `Operations updated for ${device.id} (${added} added, ${removed} removed)`,
                );
                // Something changed, so there is a next step worth naming. A no-op
                // save has nothing to say and closes as it always did.
                setSaveOutcome(
                    capabilityNextSteps(device.id, selectedOperations.length),
                );
                return;
            }
            onClose?.();
        } catch (err) {
            starekContext.showError(err);
        } finally {
            setSavingOperations(false);
        }
    };

    const handleUnlinkOperation = (operationId) => {
        setSelectedOperations((prev) =>
            prev.filter((o) => o.value !== operationId),
        );
    };

    const handleStepNavigate = (to) => {
        onClose?.();
        navigate(to);
    };

    const [signals, setSignals] = useState([]);
    const [typeSavingKey, setTypeSavingKey] = useState(null);
    // Local draft for the endpoints list's axis/UART buttons - {signalKey:
    // newType} for whatever's been clicked but not yet saved. Nothing here
    // reaches DynamoDB until handleSaveSignalTypeChanges runs, so changing
    // several ports' operational params only ever costs one save (and,
    // if needed, one reboot) instead of one round-trip per click.
    const [pendingSignalTypes, setPendingSignalTypes] = useState({});
    const [savingSignalChanges, setSavingSignalChanges] = useState(false);
    const originalSignalTypesRef = useRef({});
    // Shown after a Signals/Interpretation/Endpoint save instead of relying
    // on a pre-save "reboot now" checkbox someone has to remember to tick -
    // this device only re-reads its config at boot/reconnect, so nothing
    // just saved here takes effect until then unless rebooted now.
    const [rebootPromptOpen, setRebootPromptOpen] = useState(false);
    const [rebootingNow, setRebootingNow] = useState(false);
    const promptRebootRequired = () => setRebootPromptOpen(true);
    // Signal create/edit now happens in a modal instead of an always-visible
    // inline form, so the many protocol/binding/codec fields get the
    // screen's full attention instead of competing with the endpoint list
    // and every other signal's card for space.
    const [signalModalOpen, setSignalModalOpen] = useState(false);
    const [signalModalMode, setSignalModalMode] = useState("create");
    // Which of the modal's two sub-tabs is showing - only relevant when the
    // chosen protocol allows both (GPIO never does: sense/actuate is the
    // endpoint's own fixed property, so a direct-protocol signal only ever
    // shows Read Config). Shared between create/edit since only one mode is
    // ever visible at once.
    const [signalConfigTab, setSignalConfigTab] = useState("read");
    // Interpretations - business-context signals layered on read-only
    // physical signals (see the plan's physical vs interpretation split).
    // Each entry: {name, source, binding, codec, inverted, auxillary,
    // color}, matching updateDeviceInterpretations' wire shape exactly (see
    // 00117-starek-backend-v2's utils/signalWire.ts) - a row still in the
    // retired {analyzer, i2c_address, i2c_register} shape is normalized to
    // this on read, never written back in the old shape.
    const [interpretations, setInterpretations] = useState([]);
    // Installations - the m:n relation between a device and a Zone
    // (ShadowDevice.installed_at_zones / Zone.installed_devices in Prisma).
    // {id, name} objects rather than bare ids so the linked list can render
    // a name without a second lookup.
    const [selectedZoneId, setSelectedZoneId] = useState("");
    const [linkedZones, setLinkedZones] = useState([]);
    const [savingInstallation, setSavingInstallation] = useState(false);
    const [selectedProfileId, setSelectedProfileId] = useState("");
    const [rebootNow, setRebootNow] = useState(false);
    const [profileActionBusy, setProfileActionBusy] = useState(false);
    const [showIncompatibleProfiles, setShowIncompatibleProfiles] = useState(false);
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [showSaveAsProfileModal, setShowSaveAsProfileModal] = useState(false);
    // "+ New Interpretation" form state.
    const [newSignalName, setNewSignalName] = useState("");
    const [newSignalEndpoint, setNewSignalEndpoint] = useState("");
    const [newSignalConfig, setNewSignalConfig] = useState(
        defaultSignalConfig("i2c"),
    );
    // Independent write config (see 00117-starek-backend-v2's signalWire.ts,
    // SignalEntry.write) - off by default, meaning newSignalConfig serves
    // both directions exactly as before this existed. Always the SAME
    // physical endpoint as the read side (a different channel per direction
    // isn't supported yet) - only protocol/table/address/codec can diverge.
    // Never offered for a "direct" (GPIO) signal: sense/actuate is the
    // endpoint's own setting, not a per-signal choice.
    const [newSignalWriteEnabled, setNewSignalWriteEnabled] = useState(false);
    const [newSignalWriteConfig, setNewSignalWriteConfig] = useState(
        defaultSignalConfig("i2c"),
    );
    const [newInterpInverted, setNewInterpInverted] = useState(false);
    const [newInterpAuxillary, setNewInterpAuxillary] = useState(false);
    const [newInterpColor, setNewInterpColor] = useState(defaultSignalColors[0]);
    const [savingLogicalSignal, setSavingLogicalSignal] = useState(false);
    // Which interpretation card is expanded into its editable form - cards
    // collapse to a compact view by default (name/color/caption + an Edit
    // button). editingKey holds the interpretation's `name` being edited
    // (its identifier - interpretations, unlike physical signals, can be
    // renamed), seeded on Edit click - separate from newSignal*'s state,
    // which stays scoped to the "add a new interpretation" card.
    const [editingKey, setEditingKey] = useState(null);
    const [editName, setEditName] = useState("");
    const [editEndpoint, setEditEndpoint] = useState("");
    const [editSignalConfig, setEditSignalConfig] = useState(
        defaultSignalConfig("i2c"),
    );
    const [editSignalWriteEnabled, setEditSignalWriteEnabled] = useState(false);
    const [editSignalWriteConfig, setEditSignalWriteConfig] = useState(
        defaultSignalConfig("i2c"),
    );
    const [editInterpInverted, setEditInterpInverted] = useState(false);
    const [editInterpAuxillary, setEditInterpAuxillary] = useState(false);
    const [editInterpColor, setEditInterpColor] = useState(defaultSignalColors[0]);
    const [savingEdit, setSavingEdit] = useState(false);
    // Cross-tab navigation from a physical signal's row (Signals tab) to a
    // specific interpretation or to a pre-filled "+ New Interpretation"
    // form (Interpretation tab) - both tabs render conditionally, so the
    // actual scroll/focus has to wait until the target tab is mounted (see
    // the effects below), not fire immediately on click.
    const [highlightInterpName, setHighlightInterpName] = useState(null);
    const interpretationCardRefs = useRef({});
    // Same ConfirmModal/openConfirm/closeConfirm pattern Devices.jsx uses
    // for its own destructive actions (Unbind/Reboot/Factory reset) -
    // local to this component rather than lifted to the parent, since
    // delete/reset here are purely this tab's own concern.
    const [confirmCfg, setConfirmCfg] = useState({ open: false });
    const openConfirm = (cfg) => setConfirmCfg({ open: true, ...cfg });
    const closeConfirm = () => setConfirmCfg({ open: false });
    const [saveAsProfileName, setSaveAsProfileName] = useState("");
    const [saveAsProfileDescription, setSaveAsProfileDescription] = useState("");
    const [savingAsProfile, setSavingAsProfile] = useState(false);
    // Bumped after our own assign/unassign completes to force a resync from
    // the (by then fresh) device prop - see the effect below. Not driven by
    // device?.parameters directly, since GET_DEVICES_IN_PLANT polls every
    // 60s and that would clobber an in-progress unsaved edit.
    const [refreshTick, setRefreshTick] = useState(0);

    // What "active" signals config governs this device right now, in the
    // same precedence order as everywhere else in this feature: per-device
    // overrides win, then the assigned Device Profile's own signals, then
    // the device's stock model manifest. This tier is ONLY the profile/model
    // fallback source (see the signals-seeding effect below for where
    // device.parameters - tier 1 - is checked first) - a device with a
    // profile assigned but no per-device overrides yet has nothing in
    // device.parameters, and without this fallback the Signals tab showed
    // "No signals configured yet" despite the device actively publishing
    // data per the profile's config. The profile lookup is synchronous
    // (deviceProfiles is already a loaded prop); only the stock-model
    // fallback needs a fetch.
    const [activeManifestSignals, setActiveManifestSignals] = useState(null);
    useEffect(() => {
        if (!isOpen || !device) return;
        let cancelled = false;
        const profileId = device?.device_profile?.id;
        if (profileId) {
            const profile = deviceProfiles.find(
                (p) => String(p.id) === String(profileId),
            );
            setActiveManifestSignals(profile?.configuration?.signals || null);
            return;
        }
        if (!device?.device?.model) {
            setActiveManifestSignals(null);
            return;
        }
        fetchAvailableDeviceModels(starekContext)
            .then((models) => {
                if (cancelled) return;
                const model = models.find((m) => m.code === device.device.model);
                setActiveManifestSignals(model?.downlink?.signals || null);
            })
            .catch(() => {
                if (!cancelled) setActiveManifestSignals(null);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, device?.id, device?.device_profile?.id, device?.device?.model, deviceProfiles]);

    // Only reset the editable signal form when the sidebar opens, switches
    // to a different device, refreshTick is bumped after our own
    // assign/unassign/save completes, or activeManifestSignals finishes
    // loading (the tier-3 stock-model fetch above is async, so this effect
    // may need to re-run once it resolves) - not on every background poll,
    // or an in-progress edit would get silently clobbered out from under
    // the user.
    // Physical signals only - a hardware fact (name/bus/axis-mode), never
    // renamed, never carrying cosmetics/binding here. Sourced from the
    // manifest (which addresses exist) cross-referenced with the currently
    // applicable type per address (override, else device-reported, else
    // the manifest's own axis defaults - see manifestDefaultType).
    useEffect(() => {
        if (!isOpen || !device) return;
        const runtimeSignals = normalizeRuntimeSignals(
            device?.device?.runtime?.signals,
            // interpretation-derived entries live in runtime.interpretations,
            // not here - a binding-based one carries `ep` (or, on a
            // not-yet-normalized legacy row, `analyzer`); check both.
        ).filter((s) => s?.ep === undefined && s?.analyzer === undefined);
        const currentSignalTypes = new Map(
            runtimeSignals.map((s) => [s.name, s.type]),
        );
        const manifestEntryByAddress = {};
        if (activeManifestSignals) {
            Object.values(activeManifestSignals).forEach((entries) => {
                (Array.isArray(entries) ? entries : [entries]).forEach(
                    (entry) => {
                        const address = entry?.address || entry?.name;
                        if (address) manifestEntryByAddress[address] = entry;
                    },
                );
            });
        }
        // Manifest keys are guarded at insert above; runtime names are guarded
        // in normalizeRuntimeSignals. Filtered again here because everything
        // below treats an address as a string.
        const addresses = new Set(
            [
                ...Object.keys(manifestEntryByAddress),
                ...runtimeSignals.map((s) => s.name),
            ].filter((key) => typeof key === "string" && key),
        );
        const freshSignals = Array.from(addresses)
            .sort(compareSignalKeys)
            .map((key) => ({
                key,
                isGpio: key.startsWith("/gpio/"),
                isUart: key.startsWith("/uart"),
                manifestParams: manifestEntryByAddress[key]?.parameters,
                type:
                    currentSignalTypes.get(key) ??
                    manifestDefaultType(
                        manifestEntryByAddress[key],
                        deviceFlags,
                    ),
            }));
        setSignals(freshSignals);
        // Baseline for Discard, and for telling a genuinely-changed axis
        // button apart from a no-op click - reset on every reseed (open,
        // device switch, or our own save bumping refreshTick) since a fresh
        // seed IS the new saved baseline.
        originalSignalTypesRef.current = Object.fromEntries(
            freshSignals.map((s) => [s.key, s.type]),
        );
        setPendingSignalTypes({});
        setSelectedProfileId(device?.device_profile?.id ?? "");
        setRebootNow(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, device?.id, refreshTick, activeManifestSignals]);

    // Interpretations (business-context signals - see the plan's physical
    // vs interpretation split) are purely backend-authored, no manifest
    // merging needed - just read straight off runtime.interpretations.
    useEffect(() => {
        if (!isOpen || !device) return;
        setInterpretations(device?.device?.runtime?.interpretations || []);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, device?.id, refreshTick]);

    // ShadowDevice.parameters accumulates a cosmetics entry for every signal
    // address a device has EVER had saved against it - nothing prunes a key
    // when the device's active manifest changes (a rename, a removed
    // channel, an old firmware's virt signal that's since been dropped, or
    // switching to a Device Profile that remaps addresses entirely).
    // Cross-referencing against activeManifestSignals - the SAME tier-2/
    // tier-3 source used to seed signals above, not unconditionally the
    // device's own stock model - is what keeps orphaned entries like a
    // stale /virt/gps out, without also filtering out every profile-sourced
    // row whenever the assigned profile renames addresses (e.g. stock
    // /gpio/port0 remapped to a profile's /portA). null (not an empty Set)
    // means "not loaded yet", so the list renders unfiltered rather than
    // flashing empty.
    // Every physical signal this device actually has (all four buses -
    // GPIO ports included, not just uart/i2c) is a valid source a new
    // signal's binding can read from - derived from the same manifest
    // source the rest of this tab already uses, not a hardcoded bus list.
    const physicalEndpointOptions = useMemo(() => {
        if (!activeManifestSignals) return [];
        const options = [];
        Object.values(activeManifestSignals).forEach((entries) => {
            (Array.isArray(entries) ? entries : [entries]).forEach((entry) => {
                const address = entry?.address || entry?.name;
                if (address) options.push(address);
            });
        });
        // Object.values() here just reflects whatever order the manifest's
        // own bus keys happen to be in - not the deterministic bus/port
        // order the Signals tab (and LiveView) use, so without this sort
        // the Source dropdown's option order drifted out of sync with
        // every other listing of the same signals.
        return options.sort(compareSignalKeys);
    }, [activeManifestSignals]);

    // The same computed BBROYGBVGW color a physical signal shows in the
    // LiveView graph (see gpioTypeUtils.computePhysicalSignalColors) - used
    // here for the Source dropdown's color dot and as the default an
    // interpretation inherits from its source when it has no explicit
    // color of its own.
    const physicalSignalColors = useMemo(
        () => computePhysicalSignalColors(physicalEndpointOptions, defaultSignalColors),
        [physicalEndpointOptions, defaultSignalColors],
    );

    // Each physical address's currently applicable type (already resolved
    // by the Signals-tab seeding effect above, override > reported >
    // manifest default) - what a "direct" signal's access derives from
    // (see deriveAccess's physicalType) instead of assuming writable.
    const physicalTypeByAddress = useMemo(
        () => Object.fromEntries(signals.map((s) => [s.key, s.type])),
        [signals],
    );

    // Suggests the selected source's own computed color for a brand new
    // interpretation - an interpretation inherits its source signal's color
    // by default, unless the user overrides it here - and resets the
    // binding/codec form to that source's first legal protocol. Recomputed
    // whenever the chosen source changes (including the implicit default of
    // physicalEndpointOptions[0] before the user has touched the dropdown).
    useEffect(() => {
        const source = newSignalEndpoint || physicalEndpointOptions[0];
        setNewInterpColor(
            physicalSignalColors[source] || defaultSignalColors[0],
        );
        setNewSignalConfig(
            defaultSignalConfig(deviceProtocolOptions(source)[0]?.value || "direct"),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newSignalEndpoint, physicalEndpointOptions, physicalSignalColors]);

    // The list actually rendered - `signals` is already physical-only (see
    // the seeding effect above), so no further filtering is needed here.
    const visibleSignals = signals.map((signal, index) => ({ signal, index }));

    // A plain /uart source is a point-to-point wire with no addressing -
    // unlike I2C (addressed per-device) two signals there can genuinely
    // corrupt each other on the wire if they pick different protocols (a
    // Modbus master vs a passive raw-frame listener); several Modbus
    // signals at different addresses are fine. Mirrors the backend's own
    // check in updateDeviceInterpretations. `excludeName` leaves the entry
    // currently being edited out of its own conflict check - otherwise
    // re-saving it unchanged (or just renaming it) would flag against
    // itself.
    const uartConflictFor = (source, protocol, excludeName) => {
        if (source !== "/uart") return false;
        const inUse = new Set(
            interpretations
                .filter((i) => i.source === "/uart" && i.name !== excludeName)
                .map((i) => i.binding?.protocol)
                .filter(Boolean),
        );
        return inUse.size > 0 && !inUse.has(protocol);
    };

    // Which interpretations read from each physical signal - shown inline
    // on the Signals tab so a physical row relays its own interpretations
    // instead of requiring a trip to the Interpretation tab just to check.
    const interpretationsBySource = useMemo(() => {
        const map = {};
        interpretations.forEach((interp) => {
            if (!map[interp.source]) map[interp.source] = [];
            map[interp.source].push(interp);
        });
        return map;
    }, [interpretations]);

    // Endpoints and Signals share one tab now, so jumping from an
    // endpoint's linked-signal name to that signal's row just needs to
    // scroll/highlight it - no tab switch, no waiting for a different
    // tab's content to mount.
    useEffect(() => {
        if (activeTab !== "signals" || !highlightInterpName) return;
        interpretationCardRefs.current[highlightInterpName]?.scrollIntoView({
            behavior: "smooth",
            block: "center",
        });
        const timeout = setTimeout(() => setHighlightInterpName(null), 2000);
        return () => clearTimeout(timeout);
    }, [activeTab, highlightInterpName]);

    useEffect(() => {
        if (!isOpen || !device) return;
        setLinkedZones(device?.installed_at_zones || []);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, device?.id, refreshTick]);

    const handleAddZone = async () => {
        if (!selectedZoneId || !device?.id) return;
        const zone = zones.find((z) => String(z.id) === String(selectedZoneId));
        if (!zone || linkedZones.some((z) => z.id === zone.id)) return;
        const next = [...linkedZones, zone];
        setSavingInstallation(true);
        try {
            await onSaveInstallations?.(
                device.id,
                next.map((z) => z.id),
            );
            setLinkedZones(next);
            setSelectedZoneId("");
        } finally {
            setSavingInstallation(false);
        }
    };
    const handleRemoveZone = async (zoneId) => {
        if (!device?.id) return;
        const next = linkedZones.filter((z) => z.id !== zoneId);
        setSavingInstallation(true);
        try {
            await onSaveInstallations?.(
                device.id,
                next.map((z) => z.id),
            );
            setLinkedZones(next);
        } finally {
            setSavingInstallation(false);
        }
    };

    const updateSignal = (index, field, value) => {
        setSignals((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], [field]: value };
            return next;
        });
    };

    // A clicked axis/UART button used to fire updateDeviceSignalType
    // straight away, one network round-trip per click with no batching -
    // changing several ports in one sitting meant several saves (and,
    // since this device only applies config at boot/reconnect, no reboot
    // wiring ever actually applied to this flow). Now it only edits the
    // local draft; nothing reaches DynamoDB until "Save changes" is clicked.
    const handleTypeChange = (signal, newType) => {
        if (signal.type === newType) return;
        updateSignal(
            signals.findIndex((c) => c.key === signal.key),
            "type",
            newType,
        );
        setPendingSignalTypes((prev) => ({ ...prev, [signal.key]: newType }));
    };

    const handleDiscardSignalTypeChanges = () => {
        setSignals((prev) =>
            prev.map((s) =>
                s.key in pendingSignalTypes
                    ? { ...s, type: originalSignalTypesRef.current[s.key] }
                    : s,
            ),
        );
        setPendingSignalTypes({});
    };

    const handleSaveSignalTypeChanges = async () => {
        if (!device?.id || !onSaveLogicalSignal) return;
        const pendingKeys = Object.keys(pendingSignalTypes);
        if (pendingKeys.length === 0) return;
        setSavingSignalChanges(true);
        try {
            // overriddenSignals (not the merged runtime.signals) is the true
            // manual-override tier - start from it so a signal that was
            // never overridden before doesn't silently gain a frozen
            // profile/reported value, only the ones actually changed here.
            const existingOverrides = normalizeRuntimeSignals(
                device?.device?.overriddenSignals,
            ).filter((s) => s?.ep === undefined && s?.analyzer === undefined);
            const byName = {};
            existingOverrides.forEach((s) => {
                byName[s.name] = s;
            });
            pendingKeys.forEach((key) => {
                byName[key] = { name: key, type: pendingSignalTypes[key] };
            });
            await onSaveLogicalSignal(device.id, Object.values(byName), false);
            originalSignalTypesRef.current = {
                ...originalSignalTypesRef.current,
                ...pendingSignalTypes,
            };
            setPendingSignalTypes({});
            setRefreshTick((t) => t + 1);
            promptRebootRequired();
        } catch (e) {
            starekContext.showError?.(e?.message || "Failed to save signal changes");
        } finally {
            setSavingSignalChanges(false);
        }
    };

    // Changing one of the three axes (Discrete/Proportional, Sense/Actuate,
    // Control/State) re-decodes the port's current type to get the other
    // two axes' values, overrides the changed one, and recomposes the full
    // bitmask - same assemble/decode pair the Device Profile wizard uses.
    const handleAxisChange = (signal, axisField, axisValue) => {
        const decoded = decodeGpioType(signal.type, deviceFlags, GPIO_TYPE_AXES);
        const newType = computeGpioType(
            { ...decoded, [axisField]: axisValue },
            deviceFlags,
        );
        handleTypeChange(signal, newType);
    };

    // Same decode/override/recompose pattern as handleAxisChange, for
    // UART's 4 peripheral-init parameters instead of GPIO's 3 axes.
    const handleUartParamChange = (signal, paramField, paramValue) => {
        const decoded = decodeUartType(signal.type, signal.manifestParams, deviceFlags);
        const newType = computeUartType(
            { ...decoded, [paramField]: paramValue },
            signal.manifestParams,
            deviceFlags,
        );
        handleTypeChange(signal, newType);
    };

    // Clears one physical signal's own axis-mode override (a
    // Device.overrides.signals entry with no binding, keyed by name),
    // falling back to whatever the assigned profile or stock model reports
    // for it - same tier-2/tier-3 path used when a signal has never had an
    // override saved. Every other physical signal's override AND every
    // interpretation are re-sent unchanged, so only this one signal is
    // affected.
    const handleResetSignalToDefault = (signal) => {
        if (!device?.id || !onSaveLogicalSignal) return;
        openConfirm({
            title: `Reset ${signal.key}?`,
            message: `${signal.key}'s axis/mode override will be cleared, falling back to the assigned profile or stock model.`,
            type: "danger",
            confirmText: "Reset",
            onConfirm: async () => {
                setTypeSavingKey(signal.key);
                try {
                    // overriddenSignals (not the merged runtime.signals) -
                    // only this contains genuine overrides, so every OTHER
                    // signal here truly had one and isn't just inheriting
                    // its currently-reported value.
                    const existingOverrides = normalizeRuntimeSignals(
                        device?.device?.overriddenSignals,
                    );
                    const withoutThisSignal = existingOverrides.filter(
                        (s) =>
                            s?.ep !== undefined ||
                            s?.analyzer !== undefined ||
                            s.name !== signal.key,
                    );
                    await onSaveLogicalSignal(
                        device.id,
                        withoutThisSignal,
                        false,
                    );
                    setPendingSignalTypes((prev) => {
                        const { [signal.key]: _dropped, ...rest } = prev;
                        return rest;
                    });
                    setRefreshTick((t) => t + 1);
                    promptRebootRequired();
                } finally {
                    setTypeSavingKey(null);
                }
                closeConfirm();
            },
        });
    };

    // --- Signal handlers ---

    // Scrolls to and briefly highlights an existing signal's row, invoked
    // from a physical endpoint's "linked signals" list further up the
    // (now shared) tab.
    const handleJumpToInterpretation = (name) => {
        setHighlightInterpName(name);
    };

    const handleOpenAddSignalModal = (sourceKey) => {
        resetNewInterpretationForm();
        if (sourceKey) setNewSignalEndpoint(sourceKey);
        setSignalModalMode("create");
        setSignalConfigTab("read");
        setSignalModalOpen(true);
    };

    const resetNewInterpretationForm = () => {
        setNewSignalName("");
        setNewSignalEndpoint("");
        // Computed directly (not left to the color/config-sync effect below)
        // because clearing newSignalEndpoint to "" here is very often a
        // no-op - it's already "" from the last time this form was reset -
        // so that effect's dependency array sees no change and never
        // re-fires, leaving a stale (or hardcoded-wrong) protocol behind for
        // whatever the source now defaults to (CPRN00117: "Add Signal"
        // defaulting to /gpio/port0 but showing I2C fields and a
        // nonsensical Read/Write Config split for it).
        const defaultSource = physicalEndpointOptions[0] || "";
        setNewSignalConfig(
            defaultSignalConfig(
                deviceProtocolOptions(defaultSource)[0]?.value || "direct",
            ),
        );
        setNewSignalWriteEnabled(false);
        setNewSignalWriteConfig(defaultSignalConfig("i2c"));
        setNewInterpInverted(false);
        setNewInterpAuxillary(false);
        // Not resetting color here - clearing newSignalEndpoint above
        // already re-triggers the useEffect keyed on it, which recomputes
        // newInterpColor from physicalEndpointOptions[0]'s own color.
    };

    const handleAddInterpretation = async () => {
        const source = newSignalEndpoint || physicalEndpointOptions[0];
        const trimmedName = newSignalName.trim();
        // GPIO never splits - direction is the endpoint's own fixed
        // sense/actuate/control property, never a per-signal choice (the
        // backend rejects a `write` block on a protocol:"direct" entry
        // outright), so a stale independent write config from before the
        // source was switched to a GPIO endpoint is never sent.
        const writeSource =
            newSignalConfig.protocol !== "direct" && newSignalWriteEnabled
                ? source
                : null;
        if (
            !device?.id ||
            !onSaveInterpretations ||
            !trimmedName ||
            !source ||
            physicalEndpointOptions.includes(trimmedName) ||
            interpretations.some((i) => i.name === trimmedName) ||
            uartConflictFor(source, newSignalConfig.protocol) ||
            (writeSource && uartConflictFor(writeSource, newSignalWriteConfig.protocol))
        )
            return;
        setSavingLogicalSignal(true);
        try {
            const { binding, codec } = entryFromFormValues(newSignalConfig);
            const newEntry = {
                name: trimmedName,
                source,
                binding,
                codec,
                inverted: newInterpInverted,
                auxillary: newInterpAuxillary,
                // Only persist a color when it actually differs from the
                // source's own computed color - leaving it unset is what
                // makes "inherit from source by default" a live behavior
                // (DeviceGraph.jsx falls back to the source's color) rather
                // than a frozen snapshot of it at creation time.
                ...(newInterpColor !== physicalSignalColors[source]
                    ? { color: newInterpColor }
                    : {}),
                ...(writeSource
                    ? {
                          write: {
                              source: writeSource,
                              ...entryFromFormValues(newSignalWriteConfig),
                          },
                      }
                    : {}),
            };
            const merged = [...interpretations, newEntry];
            await onSaveInterpretations(device.id, merged, false);
            setRefreshTick((t) => t + 1);
            resetNewInterpretationForm();
            setSignalModalOpen(false);
            promptRebootRequired();
        } finally {
            setSavingLogicalSignal(false);
        }
    };

    const handleStartEditInterpretation = (interp) => {
        setEditName(interp.name);
        setEditEndpoint(interp.source || "");
        setEditSignalConfig(formValuesFromEntry(interp));
        setEditSignalWriteEnabled(!!interp.write);
        setEditSignalWriteConfig(
            formValuesFromEntry(interp.write ? { binding: interp.write.binding, codec: interp.write.codec } : {}),
        );
        setEditInterpInverted(!!interp.inverted);
        setEditInterpAuxillary(!!interp.auxillary);
        // Show the CURRENT effective color - an explicit override if one's
        // set, else whatever its source signal's computed color is right
        // now (not a stale value from whenever it was created).
        setEditInterpColor(
            interp.color ||
            physicalSignalColors[interp.source] ||
            defaultSignalColors[0],
        );
        setEditingKey(interp.name);
        setSignalModalMode("edit");
        setSignalConfigTab("read");
        setSignalModalOpen(true);
    };

    const handleCancelEdit = () => {
        setEditingKey(null);
        setSignalModalOpen(false);
    };

    const handleSaveEditInterpretation = async (originalName) => {
        if (!device?.id || !onSaveInterpretations) return;
        const trimmedName = editName.trim();
        const source = editEndpoint || physicalEndpointOptions[0];
        const writeSource =
            editSignalConfig.protocol !== "direct" && editSignalWriteEnabled
                ? source
                : null;
        if (
            !trimmedName ||
            !source ||
            physicalEndpointOptions.includes(trimmedName) ||
            interpretations.some(
                (i) => i.name === trimmedName && i.name !== originalName,
            ) ||
            uartConflictFor(source, editSignalConfig.protocol, originalName) ||
            (writeSource &&
                uartConflictFor(writeSource, editSignalWriteConfig.protocol, originalName))
        )
            return;
        setSavingEdit(true);
        try {
            const { binding, codec } = entryFromFormValues(editSignalConfig);
            const updatedEntry = {
                name: trimmedName,
                source,
                binding,
                codec,
                inverted: editInterpInverted,
                auxillary: editInterpAuxillary,
                // Same omit-if-matches-source rule as handleAddInterpretation -
                // clearing back to the source's own color here (by leaving
                // this field unset) is what lets a later source-color change
                // actually reach a previously-overridden interpretation.
                ...(editInterpColor !== physicalSignalColors[source]
                    ? { color: editInterpColor }
                    : {}),
                ...(writeSource
                    ? {
                          write: {
                              source: writeSource,
                              ...entryFromFormValues(editSignalWriteConfig),
                          },
                      }
                    : {}),
            };
            const merged = [
                ...interpretations.filter((i) => i.name !== originalName),
                updatedEntry,
            ];
            await onSaveInterpretations(device.id, merged, false);
            setRefreshTick((t) => t + 1);
            setEditingKey(null);
            setSignalModalOpen(false);
            promptRebootRequired();
        } finally {
            setSavingEdit(false);
        }
    };

    // Removing an interpretation from the submitted list is ALSO how
    // "reset to profile default" works: the backend diffs whatever's
    // submitted against parameters.profileInterpretations, so a name that
    // still exists there simply reappears (its profile version), rather
    // than actually disappearing - only the button label/confirm copy
    // needs to reflect which of those two is about to happen.
    const handleDeleteInterpretation = (interp) => {
        if (!device?.id || !onSaveInterpretations) return;
        const profileInterpretations =
            device?.device?.runtime?.profileInterpretations || [];
        const isResettable = profileInterpretations.some(
            (p) => p.name === interp.name,
        );
        openConfirm({
            title: isResettable
                ? "Reset to profile default?"
                : "Delete signal?",
            message: isResettable
                ? `"${interp.name}" will revert to the value defined by this device's assigned profile.`
                : `Delete "${interp.name}"? This can't be undone from here.`,
            type: "danger",
            confirmText: isResettable ? "Reset" : "Delete",
            onConfirm: async () => {
                setSavingEdit(true);
                try {
                    const merged = interpretations.filter(
                        (i) => i.name !== interp.name,
                    );
                    await onSaveInterpretations(device.id, merged, false);
                    setRefreshTick((t) => t + 1);
                    setEditingKey(null);
                    setSignalModalOpen(false);
                    promptRebootRequired();
                } finally {
                    setSavingEdit(false);
                }
                closeConfirm();
            },
        });
    };

    const handleAssignProfile = async () => {
        if (!device?.id || !selectedProfileId || !onAssignProfile) return;
        setProfileActionBusy(true);
        try {
            await onAssignProfile(device.id, Number(selectedProfileId), rebootNow);
            setRefreshTick((t) => t + 1);
        } finally {
            setProfileActionBusy(false);
        }
    };

    const handleUnassignProfile = async () => {
        if (!device?.id || !onUnassignProfile) return;
        setProfileActionBusy(true);
        try {
            await onUnassignProfile(device.id, rebootNow);
            setRefreshTick((t) => t + 1);
        } finally {
            setProfileActionBusy(false);
        }
    };

    // Snapshots this device's current effective config (whatever it
    // actually has right now, regardless of profile-vs-manual origin) into
    // a brand new Device Profile - the "device configured once, saved as a
    // reusable template" flow. Doesn't touch this device's own
    // device_profile_id/overrides at all.
    const handleSaveAsProfile = async () => {
        if (!device?.id || !saveAsProfileName || !onSaveAsProfile) return;
        setSavingAsProfile(true);
        try {
            await onSaveAsProfile(device.id, saveAsProfileName, saveAsProfileDescription);
            setShowSaveAsProfileModal(false);
            setSaveAsProfileName("");
            setSaveAsProfileDescription("");
        } finally {
            setSavingAsProfile(false);
        }
    };

    if (!isOpen || !device) return null;

    const assignedProfile = device.device_profile;
    // Signals are strictly one or the other now, never both at once: a
    // device either owns and can edit them directly, or a Device Profile
    // fully drives them and nothing here is editable (see the backend's
    // assignDeviceProfile/updateDeviceSignalType et al - this mirrors their
    // device_profile_id gate so the UI never offers an action the server
    // would just reject).
    const isProfileDriven = !!assignedProfile;
    const deviceModel = device.device?.model;
    // A profile's signal names/pins only make sense for the exact model it
    // was authored against (see backend's assignDeviceProfile, which
    // enforces this too) - a profile with no model set yet (still
    // unconfigured) isn't "incompatible", it just has nothing to conflict
    // with, so it's always shown.
    const isProfileCompatible = (p) => {
        const profileModel = p.configuration?.model?.code;
        return !profileModel || !deviceModel || profileModel === deviceModel;
    };
    const visibleProfiles = showIncompatibleProfiles
        ? deviceProfiles
        : deviceProfiles.filter(isProfileCompatible);
    const profileOptions = visibleProfiles.map((p) => ({
        value: p.id,
        label: p.name,
        isDisabled: !isProfileCompatible(p),
    }));
    const selectedProfileOption = selectedProfileId
        ? {
            value: selectedProfileId,
            label:
                deviceProfiles.find(
                    (p) => String(p.id) === String(selectedProfileId),
                )?.name || "",
        }
        : null;

    // Backing data for the signal modal - looked up by editingKey rather
    // than closed over from a list .map(), since the modal itself renders
    // once, outside the Signals list.
    const editingInterp = interpretations.find((i) => i.name === editingKey);
    const editingIsResettable = (
        device?.device?.runtime?.profileInterpretations || []
    ).some((p) => p.name === editingKey);
    // The modal's standalone Source field drives these - GPIO's fixed
    // sense/actuate/control access decides whether a direct-protocol source
    // gets a single labeled config section or (never, for GPIO) a Read/Write
    // tab split; every other protocol always offers both.
    const newSource = newSignalEndpoint || physicalEndpointOptions[0] || "";
    const newSourceAccess = directAccessFor(
        physicalTypeByAddress[newSource],
        deviceFlags,
    );
    const editSource = editEndpoint || physicalEndpointOptions[0] || "";
    const editSourceAccess = directAccessFor(
        physicalTypeByAddress[editSource],
        deviceFlags,
    );

    return (
        <>
            <Offcanvas
                show={isOpen}
                onHide={onClose}
                placement="end"
                backdrop={true}
                style={{
                    "--bs-offcanvas-width": "unset",
                    width: "600",
                    "--bs-offcanvas-height": "unset",
                    height: "100%",
                }}
            >
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title>
                        <h2 className="tw-text-lg tw-font-semibold">
                            {device.name || device.id}
                        </h2>
                        <div className="tw-text-sm tw-text-gray-500">
                            <strong>
                                {device.device?.model}
                                <br /> {device.device?.firmware_version}
                            </strong>
                        </div>
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    <div className="tw-h-[calc(93vh-56px)] tw-flex tw-flex-col tw-relative">
                        {onShowLiveView && (
                            <div className="tw-p-4 tw-pb-0">
                                <button
                                    type="button"
                                    onClick={() => onShowLiveView(device)}
                                    className="tw-w-full tw-px-4 tw-py-3 tw-text-base tw-font-medium tw-text-white tw-bg-blue-500 hover:tw-bg-blue-600 tw-rounded"
                                >
                                    Show LiveView
                                </button>
                            </div>
                        )}
                        <div className="tw-px-4 tw-pt-2 tw-pb-0 tw-flex">
                            <button
                                type="button"
                                onClick={() => setShowProfileModal(true)}
                                className={`tw-flex-1 tw-px-4 tw-py-3 tw-text-base tw-font-medium tw-text-gray-900 tw-bg-yellow-400 hover:tw-bg-yellow-500 ${canWrite && onSaveAsProfile
                                        ? "tw-rounded-l"
                                        : "tw-rounded"
                                    }`}
                            >
                                Apply Device Profile
                            </button>
                            {canWrite && onSaveAsProfile && (
                                <Dropdown align="end">
                                    <Dropdown.Toggle
                                        as="button"
                                        className="tw-h-full tw-px-3 tw-text-gray-900 tw-bg-yellow-400 hover:tw-bg-yellow-500 tw-rounded-r tw-border-l tw-border-yellow-600"
                                    />
                                    <Dropdown.Menu>
                                        {/* Rarer, more deliberate action than "Apply" -
                                        tucked behind the caret so it's not equally
                                        weighted next to the primary button and
                                        harder to hit by accident. */}
                                        <Dropdown.Item
                                            onClick={() => setShowSaveAsProfileModal(true)}
                                        >
                                            Save Device Profile
                                        </Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>
                            )}
                        </div>
                        <div className="tw-flex tw-border-b tw-overflow-x-auto">
                            {SIDEBAR_TABS.map((tab) => (
                                <button
                                    key={tab.key}
                                    type="button"
                                    className={`tw-px-4 tw-py-3 tw-text-sm tw-whitespace-nowrap tw-shrink-0 ${activeTab === tab.key
                                        ? "tw-border-b-2 tw-border-blue-500 tw-text-blue-500"
                                        : "tw-text-gray-500"
                                        }`}
                                    onClick={() => setActiveTab(tab.key)}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        {profileActionBusy && (
                            <div className="tw-absolute tw-inset-0 tw-top-12 tw-bg-white/70 tw-flex tw-items-center tw-justify-center tw-z-10">
                                <div
                                    className="tw-w-8 tw-h-8 tw-border-4 tw-border-blue-500 tw-border-t-transparent tw-rounded-full tw-animate-spin"
                                    role="status"
                                    aria-label="Working"
                                />
                            </div>
                        )}

                        <div className="tw-flex-1 tw-overflow-y-auto">
                            {activeTab === "signals" && (
                                <div className="tw-p-4">
                                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
                                        <h3 className="tw-text-sm tw-font-semibold tw-text-gray-700">
                                            Endpoints
                                        </h3>
                                    </div>
                                    {isProfileDriven ? (
                                        <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-mb-3 tw-px-3 tw-py-2 tw-bg-yellow-50 tw-border tw-border-yellow-200 tw-rounded tw-text-sm">
                                            <span className="tw-text-yellow-900">
                                                Signals are driven by Device
                                                Profile{" "}
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        onClose?.();
                                                        navigate(
                                                            `/device-profiles/${assignedProfile.id}`,
                                                        );
                                                    }}
                                                    className="tw-font-medium tw-underline hover:tw-no-underline"
                                                >
                                                    {assignedProfile.name}
                                                </button>{" "}
                                                and can't be changed here.
                                            </span>
                                            {canWrite && onUnassignProfile && (
                                                <button
                                                    type="button"
                                                    onClick={handleUnassignProfile}
                                                    disabled={profileActionBusy}
                                                    className="tw-shrink-0 tw-px-3 tw-py-1 tw-text-xs tw-font-medium tw-text-gray-900 tw-bg-yellow-400 hover:tw-bg-yellow-500 tw-rounded disabled:tw-opacity-50"
                                                >
                                                    {profileActionBusy
                                                        ? "Working..."
                                                        : "Unassign Profile"}
                                                </button>
                                            )}
                                        </div>
                                    ) : (
                                        <p className="tw-text-sm tw-text-gray-500 tw-mb-3">
                                            This device manages its own
                                            endpoints and signals directly.
                                            Give a port a friendly name and set
                                            up how its data is read or written
                                            in the Signals list below.
                                        </p>
                                    )}
                                    {!isProfileDriven && Object.keys(pendingSignalTypes).length > 0 && (
                                        <div className="tw-sticky tw-top-0 tw-z-10 tw-flex tw-items-center tw-justify-between tw-gap-3 tw-mb-3 tw-px-3 tw-py-2 tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded tw-text-sm">
                                            <span className="tw-text-blue-900">
                                                {Object.keys(pendingSignalTypes).length}{" "}
                                                unsaved change
                                                {Object.keys(pendingSignalTypes).length === 1
                                                    ? ""
                                                    : "s"}
                                            </span>
                                            <div className="tw-flex tw-gap-2">
                                                <button
                                                    type="button"
                                                    onClick={handleDiscardSignalTypeChanges}
                                                    disabled={savingSignalChanges}
                                                    className="tw-px-3 tw-py-1 tw-text-xs tw-rounded tw-bg-white tw-border tw-border-gray-300 tw-text-gray-600 hover:tw-bg-gray-50 disabled:tw-opacity-50"
                                                >
                                                    Discard
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={handleSaveSignalTypeChanges}
                                                    disabled={savingSignalChanges}
                                                    className="tw-px-3 tw-py-1 tw-text-xs tw-rounded tw-bg-blue-600 tw-text-white hover:tw-bg-blue-700 disabled:tw-opacity-50"
                                                >
                                                    {savingSignalChanges
                                                        ? "Saving..."
                                                        : "Save changes"}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    {visibleSignals.length === 0 && (
                                        <p className="tw-text-sm tw-text-gray-400">
                                            No endpoints configured yet.
                                        </p>
                                    )}
                                    {visibleSignals.map(({ signal }) => (
                                        <div
                                            key={signal.key}
                                            className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap tw-py-2 tw-px-1 tw-border-b tw-border-gray-100"
                                        >
                                            <div className="tw-flex-1 tw-min-w-0">
                                                <div className="tw-text-sm tw-truncate tw-font-mono">
                                                    {signal.key}
                                                </div>
                                                {(interpretationsBySource[signal.key] || [])
                                                    .length > 0 && (
                                                    <div className="tw-flex tw-items-center tw-flex-wrap tw-gap-x-2 tw-text-xs tw-mt-0.5">
                                                        {(
                                                            interpretationsBySource[
                                                                signal.key
                                                            ] || []
                                                        ).map((interp) => (
                                                            <button
                                                                key={interp.name}
                                                                type="button"
                                                                title={`Go to signal "${interp.name}"`}
                                                                onClick={() =>
                                                                    handleJumpToInterpretation(
                                                                        interp.name,
                                                                    )
                                                                }
                                                                className="tw-text-blue-600 hover:tw-underline"
                                                            >
                                                                {interp.name}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            {canWrite && onSaveLogicalSignal && !isProfileDriven && (
                                                <button
                                                    type="button"
                                                    title={`Reset ${signal.key} to default`}
                                                    onClick={() =>
                                                        handleResetSignalToDefault(signal)
                                                    }
                                                    disabled={
                                                        typeSavingKey === signal.key ||
                                                        signal.key in pendingSignalTypes
                                                    }
                                                    className="tw-w-7 tw-h-7 tw-flex tw-items-center tw-justify-center tw-rounded disabled:tw-opacity-50 tw-text-gray-400 hover:tw-bg-gray-100 hover:tw-text-gray-600 tw-shrink-0"
                                                >
                                                    <i className="fa fa-undo" />
                                                </button>
                                            )}
                                            {signal.isGpio && (
                                                <div className="tw-flex tw-items-center tw-flex-wrap tw-gap-2">
                                                        {(() => {
                                                            const decoded = decodeGpioType(
                                                                signal.type,
                                                                deviceFlags,
                                                                GPIO_TYPE_AXES,
                                                            );
                                                            return GPIO_TYPE_AXES.map((axis) => {
                                                                const current =
                                                                    decoded[axis.name] ?? axis.options[0];
                                                                const other =
                                                                    axis.options.find(
                                                                        (o) => o !== current,
                                                                    ) ?? axis.options[0];
                                                                const info = AXIS_ICON_INFO[current];
                                                                return (
                                                                    <button
                                                                        key={axis.name}
                                                                        type="button"
                                                                        title={info?.help}
                                                                        disabled={
                                                                            !canWrite ||
                                                                            isProfileDriven ||
                                                                            typeSavingKey === signal.key
                                                                        }
                                                                        onClick={() =>
                                                                            handleAxisChange(
                                                                                signal,
                                                                                axis.name,
                                                                                other,
                                                                            )
                                                                        }
                                                                        className={`tw-w-7 tw-h-7 tw-flex tw-items-center tw-justify-center tw-rounded disabled:tw-opacity-50 ${info?.colorClass || "tw-bg-blue-50 tw-text-blue-600 hover:tw-bg-blue-100"}`}
                                                                    >
                                                                        <i className={`fa ${info?.icon}`} />
                                                                    </button>
                                                                );
                                                            });
                                                        })()}
                                                    </div>
                                                )}
                                                {signal.isUart && (
                                                    <div className="tw-flex tw-items-center tw-flex-wrap tw-gap-2">
                                                        {(() => {
                                                            const decoded = decodeUartType(
                                                                signal.type,
                                                                signal.manifestParams,
                                                                deviceFlags,
                                                            );
                                                            return UART_PARAMS.map((param) => {
                                                                const manifestParam =
                                                                    signal.manifestParams?.find(
                                                                        (p) => p.name === param.name,
                                                                    );
                                                                const options = manifestParam?.range || [];
                                                                if (options.length === 0) return null;
                                                                const current =
                                                                    decoded[param.name] ??
                                                                    manifestParam?.default ??
                                                                    "";
                                                                return (
                                                                    <select
                                                                        key={param.name}
                                                                        title={param.label}
                                                                        disabled={
                                                                            !canWrite ||
                                                                            isProfileDriven ||
                                                                            typeSavingKey === signal.key
                                                                        }
                                                                        value={current}
                                                                        onChange={(e) =>
                                                                            handleUartParamChange(
                                                                                signal,
                                                                                param.name,
                                                                                e.target.value,
                                                                            )
                                                                        }
                                                                        className="tw-text-xs tw-border tw-rounded tw-px-1 tw-py-0.5 disabled:tw-opacity-50"
                                                                    >
                                                                        {options.map((opt) => (
                                                                            <option key={opt} value={opt}>
                                                                                {param.label}: {opt}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                );
                                                            });
                                                        })()}
                                                    </div>
                                                )}
                                        </div>
                                    ))}
                                </div>
                            )}
                            {activeTab === "signals" && (
                                <div className="tw-p-4 tw-pt-0">
                                    <div className="tw-flex tw-items-center tw-justify-between tw-mt-5 tw-mb-3 tw-pt-4 tw-border-t">
                                        <h3 className="tw-text-sm tw-font-semibold tw-text-gray-700">
                                            Signals
                                        </h3>
                                    </div>
                                    <p className="tw-text-sm tw-text-gray-500 tw-mb-4">
                                        Give a signal a friendly name and choose how to
                                        read (and, if needed, write) its data.
                                    </p>
                                    {interpretations.length === 0 && (
                                        <p className="tw-text-sm tw-text-gray-400">
                                            No signals configured yet.
                                        </p>
                                    )}
                                    {interpretations.map((interp) => {
                                        const isHighlighted = highlightInterpName === interp.name;
                                        return (
                                            <div
                                                key={interp.name}
                                                ref={(el) => {
                                                    interpretationCardRefs.current[interp.name] = el;
                                                }}
                                                className={`tw-border tw-rounded tw-p-3 tw-mb-3 tw-transition-colors ${isHighlighted
                                                        ? "tw-border-blue-400 tw-bg-blue-50"
                                                        : ""
                                                    }`}
                                            >
                                                <div className="tw-flex tw-items-center tw-gap-2">
                                                    <span
                                                        className="tw-w-3 tw-h-3 tw-rounded-full tw-shrink-0"
                                                        style={{
                                                            backgroundColor: `#${interp.color ||
                                                                physicalSignalColors[
                                                                interp.source
                                                                ] ||
                                                                "0080ff"
                                                                }`,
                                                        }}
                                                    />
                                                    <div className="tw-flex-1 tw-min-w-0">
                                                        <div className="tw-text-sm tw-truncate">
                                                            {interp.name}
                                                        </div>
                                                        <div className="tw-text-xs tw-text-gray-400 tw-truncate">
                                                            via {interp.source} ·{" "}
                                                            {interp.binding?.protocol}
                                                            {interp.binding?.table &&
                                                                ` (${interp.binding.table})`}
                                                            {" · "}
                                                            {(() => {
                                                                const access = deriveAccess({
                                                                    protocol: interp.binding?.protocol,
                                                                    table: interp.binding?.table,
                                                                    codecKind: interp.codec?.kind,
                                                                    readOnly: interp.binding?.read_only,
                                                                    physicalType:
                                                                        physicalTypeByAddress[
                                                                            interp.source
                                                                        ],
                                                                    deviceFlags,
                                                                });
                                                                if (access.read && access.write)
                                                                    return "R/W";
                                                                return access.write
                                                                    ? "Write-only"
                                                                    : "Read-only";
                                                            })()}
                                                            {interp.inverted && " · Inverted"}
                                                            {interp.auxillary && " · Auxiliary"}
                                                        </div>
                                                    </div>
                                                    {canWrite && !isProfileDriven && (
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                handleStartEditInterpretation(
                                                                    interp,
                                                                )
                                                            }
                                                            className="tw-px-2 tw-py-1 tw-text-xs tw-rounded tw-bg-gray-100 tw-text-gray-600 hover:tw-bg-gray-200 tw-shrink-0"
                                                        >
                                                            Edit
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {canWrite && onSaveInterpretations && !isProfileDriven && (
                                        <button
                                            type="button"
                                            onClick={() => handleOpenAddSignalModal()}
                                            className="tw-w-full tw-px-4 tw-py-2 tw-text-sm tw-text-white tw-bg-blue-500 hover:tw-bg-blue-600 tw-rounded tw-mt-2"
                                        >
                                            + Add Signal
                                        </button>
                                    )}
                                </div>
                            )}
                            {activeTab === "attributes" && (
                                <DeviceAttributesPanel device={device} />
                            )}
                            {activeTab === "locations" && (
                                <div className="tw-p-4">
                                    <p className="tw-text-sm tw-text-gray-500 tw-mb-4">
                                        An Installation links this device to a zone -
                                        where it's physically set up on the floor.
                                    </p>
                                    {canWrite && (
                                        <div className="tw-mb-6">
                                            <label className="tw-block tw-mb-2">
                                                <span className="tw-text-sm tw-font-medium">
                                                    Zone
                                                </span>
                                            </label>
                                            <div className="tw-flex tw-gap-2">
                                                <select
                                                    value={selectedZoneId}
                                                    onChange={(e) =>
                                                        setSelectedZoneId(e.target.value)
                                                    }
                                                    className="tw-flex-1 tw-form-select tw-block tw-w-full tw-rounded-md tw-border tw-border-gray-300 tw-px-3 tw-py-2"
                                                >
                                                    <option value="">Select zone</option>
                                                    {zones
                                                        .filter(
                                                            (zone) =>
                                                                !linkedZones.some(
                                                                    (z) => z.id === zone.id,
                                                                ),
                                                        )
                                                        .map((zone) => (
                                                            <option
                                                                key={zone.id}
                                                                value={zone.id}
                                                            >
                                                                {zone.name}
                                                            </option>
                                                        ))}
                                                </select>
                                                <button
                                                    onClick={handleAddZone}
                                                    disabled={
                                                        !selectedZoneId || savingInstallation
                                                    }
                                                    className="tw-px-4 tw-py-2 tw-text-sm tw-text-white tw-bg-blue-500 hover:tw-bg-blue-600 tw-rounded disabled:tw-opacity-50"
                                                >
                                                    Add
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    <div>
                                        <h3 className="tw-text-sm tw-font-medium tw-mb-3">
                                            Installations
                                        </h3>
                                        {linkedZones.length === 0 && (
                                            <p className="tw-text-sm tw-text-gray-400">
                                                Not installed at any zone yet.
                                            </p>
                                        )}
                                        <div className="tw-space-y-2">
                                            {linkedZones.map((zone) => (
                                                <div
                                                    key={zone.id}
                                                    className="tw-flex tw-justify-between tw-items-center tw-p-3 tw-border tw-rounded-md"
                                                >
                                                    <span className="tw-text-sm">
                                                        {zone.name}
                                                    </span>
                                                    {canWrite && (
                                                        <button
                                                            onClick={() =>
                                                                handleRemoveZone(zone.id)
                                                            }
                                                            disabled={savingInstallation}
                                                            className="tw-text-gray-400 hover:tw-text-red-500 disabled:tw-opacity-50"
                                                            title={`Remove from ${zone.name}`}
                                                        >
                                                            <MdClose className="tw-w-5 tw-h-5" />
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                            {activeTab === "operations" &&
                                (saveOutcome ? (
                                    <div className="tw-p-4">
                                        <SaveSuccessPanel
                                            heading={saveOutcome.heading}
                                            subheading={saveOutcome.subheading}
                                            steps={saveOutcome.steps}
                                            onNavigate={handleStepNavigate}
                                        />
                                        <div className="tw-mt-4 tw-flex tw-justify-end">
                                            <button
                                                onClick={onClose}
                                                className="tw-px-4 tw-py-2 tw-text-sm tw-text-white tw-bg-blue-500 hover:tw-bg-blue-600 tw-rounded"
                                            >
                                                Done
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="tw-p-4">
                                        <p className="tw-text-sm tw-text-gray-500 tw-mb-4">
                                            Operations this device can perform. This
                                            is what makes it eligible when a job
                                            schedule needs that operation.
                                        </p>

                                        <label className="tw-block tw-mb-2">
                                            <span className="tw-text-sm tw-font-medium">
                                                Operations
                                            </span>
                                        </label>
                                        <Select
                                            options={operationOptions}
                                            placeholder={
                                                loadingOperations
                                                    ? "Loading operations..."
                                                    : "Select operations"
                                            }
                                            isMulti
                                            isDisabled={
                                                !canWrite ||
                                                loadingOperations ||
                                                loadingCapabilities
                                            }
                                            value={selectedOperations}
                                            onChange={(val) =>
                                                setSelectedOperations(val || [])
                                            }
                                            components={{ MultiValue: () => null }}
                                            closeMenuOnSelect={true}
                                            hideSelectedOptions={true}
                                            menuPortalTarget={document.body}
                                            styles={selectMenuPortalStyles}
                                        />

                                        {operationOptions.length === 0 &&
                                            !loadingOperations && (
                                                <div className="tw-text-sm tw-text-gray-500 tw-mt-3">
                                                    No operations defined for this
                                                    plant yet. Add them under Setup
                                                    &rarr; Operations.
                                                </div>
                                            )}

                                        <div className="tw-mt-6">
                                            <h3 className="tw-text-sm tw-font-medium tw-mb-3">
                                                Linked Operations
                                            </h3>
                                            {selectedOperations.length === 0 ? (
                                                <div className="tw-text-sm tw-text-gray-500">
                                                    No operations linked. This
                                                    device will not be recommended
                                                    for any operation.
                                                </div>
                                            ) : (
                                                <div className="tw-space-y-2">
                                                    {selectedOperations.map(
                                                        (operation) => (
                                                            <div
                                                                key={operation.value}
                                                                className="tw-flex tw-justify-between tw-items-center tw-p-3 tw-border tw-rounded-md"
                                                            >
                                                                <span className="tw-text-sm">
                                                                    {operation.label}
                                                                </span>
                                                                {canWrite && (
                                                                    <button
                                                                        onClick={() =>
                                                                            handleUnlinkOperation(
                                                                                operation.value,
                                                                            )
                                                                        }
                                                                        className="tw-text-gray-400 hover:tw-text-red-500"
                                                                        title={`Unlink ${operation.label}`}
                                                                    >
                                                                        <i
                                                                            className="fa fa-unlink"
                                                                            aria-hidden="true"
                                                                        />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        ),
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        <div className="tw-mt-6 tw-flex tw-justify-end tw-gap-3">
                                            <button
                                                onClick={onClose}
                                                className="tw-px-4 tw-py-2 tw-text-sm tw-text-gray-600 tw-border tw-rounded"
                                            >
                                                Discard
                                            </button>
                                            <button
                                                onClick={handleSaveOperations}
                                                disabled={
                                                    !canWrite || savingOperations
                                                }
                                                className="tw-px-4 tw-py-2 tw-text-sm tw-text-white tw-bg-blue-500 hover:tw-bg-blue-600 tw-rounded disabled:tw-opacity-50"
                                            >
                                                {savingOperations
                                                    ? "Saving..."
                                                    : "Save & Update"}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                        </div>
                    </div>
                </Offcanvas.Body>
            </Offcanvas>
            <Modal
                show={showProfileModal}
                onHide={() => setShowProfileModal(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Apply Device Profile</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="tw-mb-3">
                        <label className="tw-block tw-mb-2">
                            <span className="tw-text-sm tw-font-medium">
                                Device Profile
                            </span>
                        </label>
                        <Select
                            isDisabled={!canWrite}
                            options={profileOptions}
                            value={selectedProfileOption}
                            onChange={(opt) =>
                                setSelectedProfileId(opt ? opt.value : "")
                            }
                            isOptionDisabled={(opt) => opt.isDisabled}
                            placeholder="-- Select a profile --"
                            isClearable
                            isSearchable
                            menuPortalTarget={document.body}
                            styles={selectMenuPortalStyles}
                        />
                        {deviceModel && (
                            <label className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-gray-500 tw-mt-2">
                                <input
                                    type="checkbox"
                                    checked={showIncompatibleProfiles}
                                    onChange={(e) =>
                                        setShowIncompatibleProfiles(
                                            e.target.checked,
                                        )
                                    }
                                />
                                Show incompatible profiles
                            </label>
                        )}
                    </div>

                    <label className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-600 tw-mb-4">
                        <input
                            type="checkbox"
                            disabled={!canWrite}
                            checked={rebootNow}
                            onChange={(e) =>
                                setRebootNow(e.target.checked)
                            }
                        />
                        Reboot device now to apply signal role
                        changes immediately
                    </label>

                    <p className="tw-text-xs tw-text-gray-400 tw-mb-4">
                        Assigning a profile fully takes over this
                        device's signals - the Signals tab locks to the
                        profile's own values and stops being editable
                        here until it's unassigned again, which brings
                        back whatever this device had configured before.
                        Attribute changes are live now; signal role
                        changes take effect the next time this device
                        reconnects, unless you reboot it now.
                    </p>

                    {canWrite && (
                        <div className="tw-flex tw-gap-2">
                            <button
                                onClick={handleAssignProfile}
                                disabled={
                                    !selectedProfileId ||
                                    profileActionBusy ||
                                    selectedProfileId ===
                                    String(assignedProfile?.id ?? "")
                                }
                                className="tw-flex-1 tw-px-4 tw-py-2 tw-text-sm tw-text-white tw-bg-blue-500 hover:tw-bg-blue-600 tw-rounded disabled:tw-opacity-50"
                            >
                                {profileActionBusy
                                    ? "Working..."
                                    : assignedProfile
                                        ? "Switch Profile"
                                        : "Assign Profile"}
                            </button>
                            {assignedProfile && (
                                <button
                                    onClick={handleUnassignProfile}
                                    disabled={profileActionBusy}
                                    className="tw-flex-1 tw-px-4 tw-py-2 tw-text-sm tw-text-red-600 tw-border tw-border-red-300 hover:tw-bg-red-50 tw-rounded disabled:tw-opacity-50"
                                >
                                    Unassign
                                </button>
                            )}
                        </div>
                    )}
                </Modal.Body>
            </Modal>
            <Modal
                show={showSaveAsProfileModal}
                onHide={() => setShowSaveAsProfileModal(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Save Device Profile</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="tw-text-xs tw-text-gray-400 tw-mb-4">
                        Snapshots this device's current signals, interpretations,
                        and attributes into a brand new, reusable profile. Doesn't
                        change anything about this device itself.
                    </p>
                    <label className="tw-block tw-mb-2">
                        <span className="tw-text-sm tw-font-medium">
                            Profile Name
                        </span>
                    </label>
                    <input
                        type="text"
                        value={saveAsProfileName}
                        onChange={(e) => setSaveAsProfileName(e.target.value)}
                        placeholder="e.g. Line 3 Sewing Machine"
                        className="tw-block tw-w-full tw-rounded-md tw-border tw-border-gray-300 tw-px-3 tw-py-2 tw-mb-2"
                    />
                    <label className="tw-block tw-mb-2">
                        <span className="tw-text-sm tw-font-medium">
                            Description
                        </span>
                    </label>
                    <textarea
                        value={saveAsProfileDescription}
                        onChange={(e) =>
                            setSaveAsProfileDescription(e.target.value)
                        }
                        placeholder="Optional description..."
                        rows={2}
                        className="tw-block tw-w-full tw-rounded-md tw-border tw-border-gray-300 tw-px-3 tw-py-2 tw-mb-3"
                    ></textarea>
                    <button
                        onClick={handleSaveAsProfile}
                        disabled={!saveAsProfileName || savingAsProfile}
                        className="tw-w-full tw-px-4 tw-py-2 tw-text-sm tw-text-white tw-bg-blue-500 hover:tw-bg-blue-600 tw-rounded disabled:tw-opacity-50"
                    >
                        {savingAsProfile ? "Saving..." : "Save as Profile"}
                    </button>
                </Modal.Body>
            </Modal>
            <ConfirmModal
                open={confirmCfg.open}
                type={confirmCfg.type}
                title={confirmCfg.title}
                message={confirmCfg.message}
                confirmText={confirmCfg.confirmText}
                onConfirm={confirmCfg.onConfirm}
                onClose={closeConfirm}
            />
            {/* Shown after every Endpoint/Signal save instead of a pre-save
                "reboot now" checkbox someone has to remember to tick - this
                device only re-reads its config at boot/reconnect, so nothing
                just saved here takes effect until it does. */}
            <ConfirmModal
                open={rebootPromptOpen}
                type="info"
                title="Reboot required to apply this"
                message="Saved. This device won't pick up the change until it reboots or reconnects - it'll apply on its own eventually, or you can reboot it now."
                customButtons={[
                    { label: "Later", action: "cancel" },
                    {
                        label: rebootingNow ? "Rebooting..." : "Reboot now",
                        action: "custom",
                        onClick: async () => {
                            if (!device?.id || !onRebootDevice) {
                                setRebootPromptOpen(false);
                                return;
                            }
                            setRebootingNow(true);
                            try {
                                await onRebootDevice(device.id);
                            } finally {
                                setRebootingNow(false);
                                setRebootPromptOpen(false);
                            }
                        },
                    },
                ]}
                onClose={() => setRebootPromptOpen(false)}
            />
            {/* Signal create/edit - moved out of the inline list into a modal
                so the protocol/binding/codec fields (and, when split, a whole
                second read+write config) get the screen's full attention
                instead of competing with every other signal's card. */}
            <Modal
                show={signalModalOpen}
                onHide={handleCancelEdit}
                centered
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {signalModalMode === "edit"
                            ? `Edit "${editingKey}"`
                            : "Add Signal"}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {signalModalMode === "create" ? (
                        <>
                            <label className="tw-text-xs tw-text-gray-500 tw-block tw-mb-1">
                                Name
                            </label>
                            <input
                                type="text"
                                value={newSignalName}
                                onChange={(e) => setNewSignalName(e.target.value)}
                                placeholder="e.g. soil sensor"
                                className="tw-w-full tw-px-2 tw-py-1 tw-text-sm tw-border tw-rounded tw-mb-2"
                                autoFocus
                            />
                            {newSignalName.trim() &&
                                (physicalEndpointOptions.includes(
                                    newSignalName.trim(),
                                ) ||
                                    interpretations.some(
                                        (i) => i.name === newSignalName.trim(),
                                    )) && (
                                    <p className="tw-text-xs tw-text-red-600 tw-mb-2">
                                        That name is already in use - pick a
                                        different one.
                                    </p>
                                )}

                            <label className="tw-text-xs tw-text-gray-500 tw-block tw-mb-1">
                                Source (which physical endpoint to use)
                            </label>
                            <Select
                                options={physicalEndpointOptions.map((ep) => ({
                                    value: ep,
                                    label: ep,
                                    color: physicalSignalColors[ep],
                                }))}
                                value={{
                                    value: newSource,
                                    label: newSource,
                                    color: physicalSignalColors[newSource],
                                }}
                                onChange={(opt) => {
                                    const ep = opt ? opt.value : "";
                                    setNewSignalEndpoint(ep);
                                    setNewSignalConfig(
                                        defaultSignalConfig(
                                            deviceProtocolOptions(ep)[0]?.value ||
                                            "direct",
                                        ),
                                    );
                                    // A forked write config was only ever
                                    // valid for the old endpoint's protocol
                                    // options - start it over, mirroring read
                                    // again, rather than carry stale settings
                                    // to a different endpoint.
                                    setNewSignalWriteEnabled(false);
                                    setSignalConfigTab("read");
                                }}
                                formatOptionLabel={formatSourceOption}
                                menuPortalTarget={document.body}
                                styles={selectMenuPortalStyles}
                                className="tw-mb-2 tw-text-sm"
                            />

                            {newSignalConfig.protocol === "direct" ? (
                                <>
                                    <div className="tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-2">
                                        {directAccessLabel(newSourceAccess)}
                                    </div>
                                    <ProtocolAndConfigFields
                                        endpoint={newSource}
                                        config={newSignalConfig}
                                        onConfigChange={setNewSignalConfig}
                                        physicalTypeByAddress={physicalTypeByAddress}
                                        deviceFlags={deviceFlags}
                                        uartConflictWarning={uartConflictFor(
                                            newSource,
                                            newSignalConfig.protocol,
                                        )}
                                    />
                                </>
                            ) : (
                                <>
                                    <div className="tw-flex tw-gap-4 tw-mb-3 tw-border-b tw-border-gray-200">
                                        <button
                                            type="button"
                                            onClick={() => setSignalConfigTab("read")}
                                            className={`tw-px-1 tw-pb-2 tw-text-sm tw-border-b-2 ${signalConfigTab === "read"
                                                ? "tw-border-blue-500 tw-text-blue-600 tw-font-medium"
                                                : "tw-border-transparent tw-text-gray-500"
                                                }`}
                                        >
                                            Read Config
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setSignalConfigTab("write")}
                                            className={`tw-px-1 tw-pb-2 tw-text-sm tw-border-b-2 ${signalConfigTab === "write"
                                                ? "tw-border-blue-500 tw-text-blue-600 tw-font-medium"
                                                : "tw-border-transparent tw-text-gray-500"
                                                }`}
                                        >
                                            Write Config
                                        </button>
                                    </div>

                                    {signalConfigTab === "read" && (
                                        <ProtocolAndConfigFields
                                            endpoint={newSource}
                                            config={newSignalConfig}
                                            onConfigChange={setNewSignalConfig}
                                            physicalTypeByAddress={physicalTypeByAddress}
                                            deviceFlags={deviceFlags}
                                            uartConflictWarning={uartConflictFor(
                                                newSource,
                                                newSignalConfig.protocol,
                                            )}
                                        />
                                    )}

                                    {signalConfigTab === "write" && (
                                        <ProtocolAndConfigFields
                                            endpoint={newSource}
                                            config={
                                                newSignalWriteEnabled
                                                    ? newSignalWriteConfig
                                                    : newSignalConfig
                                            }
                                            onConfigChange={(cfg) => {
                                                setNewSignalWriteEnabled(true);
                                                setNewSignalWriteConfig(cfg);
                                            }}
                                            physicalTypeByAddress={physicalTypeByAddress}
                                            deviceFlags={deviceFlags}
                                            uartConflictWarning={uartConflictFor(
                                                newSource,
                                                newSignalWriteEnabled
                                                    ? newSignalWriteConfig.protocol
                                                    : newSignalConfig.protocol,
                                            )}
                                        />
                                    )}
                                </>
                            )}

                            <label className="tw-text-xs tw-text-gray-500 tw-block tw-mb-1 tw-mt-2">
                                Color
                            </label>
                            <input
                                type="color"
                                value={`#${newInterpColor}`}
                                onChange={(e) =>
                                    setNewInterpColor(
                                        e.target.value.replace("#", ""),
                                    )
                                }
                                className="tw-w-full tw-h-8 tw-border tw-rounded tw-mb-2"
                            />

                            <div className="tw-flex tw-items-center tw-gap-3 tw-mb-2">
                                <label className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-600">
                                    <input
                                        type="checkbox"
                                        checked={newInterpInverted}
                                        onChange={(e) =>
                                            setNewInterpInverted(e.target.checked)
                                        }
                                    />
                                    Inverted
                                </label>
                                <label className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-600">
                                    <input
                                        type="checkbox"
                                        checked={newInterpAuxillary}
                                        onChange={(e) =>
                                            setNewInterpAuxillary(e.target.checked)
                                        }
                                    />
                                    Auxiliary
                                </label>
                            </div>
                        </>
                    ) : (
                        editingInterp && (
                            <>
                                <label className="tw-text-xs tw-text-gray-500 tw-block tw-mb-1">
                                    Name
                                </label>
                                <input
                                    type="text"
                                    value={editName}
                                    disabled={!canWrite}
                                    onChange={(e) => setEditName(e.target.value)}
                                    className="tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded tw-text-sm tw-mb-2"
                                    placeholder="e.g. soil sensor"
                                />

                                <label className="tw-text-xs tw-text-gray-500 tw-block tw-mb-1">
                                    Source (which physical endpoint to use)
                                </label>
                                <Select
                                    options={physicalEndpointOptions.map((ep) => ({
                                        value: ep,
                                        label: ep,
                                        color: physicalSignalColors[ep],
                                    }))}
                                    value={{
                                        value: editSource,
                                        label: editSource,
                                        color: physicalSignalColors[editSource],
                                    }}
                                    onChange={(opt) => {
                                        const ep = opt ? opt.value : "";
                                        setEditEndpoint(ep);
                                        setEditSignalConfig(
                                            defaultSignalConfig(
                                                deviceProtocolOptions(ep)[0]?.value ||
                                                "direct",
                                            ),
                                        );
                                        // A forked write config was only ever
                                        // valid for the old endpoint's protocol
                                        // options - start it over, mirroring
                                        // read again, rather than carry stale
                                        // settings to a different endpoint.
                                        setEditSignalWriteEnabled(false);
                                        setSignalConfigTab("read");
                                    }}
                                    formatOptionLabel={formatSourceOption}
                                    menuPortalTarget={document.body}
                                    styles={selectMenuPortalStyles}
                                    className="tw-mb-2 tw-text-sm"
                                />

                                {editSignalConfig.protocol === "direct" ? (
                                    <>
                                        <div className="tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-2">
                                            {directAccessLabel(editSourceAccess)}
                                        </div>
                                        <ProtocolAndConfigFields
                                            endpoint={editSource}
                                            config={editSignalConfig}
                                            onConfigChange={setEditSignalConfig}
                                            physicalTypeByAddress={physicalTypeByAddress}
                                            deviceFlags={deviceFlags}
                                            uartConflictWarning={uartConflictFor(
                                                editSource,
                                                editSignalConfig.protocol,
                                                editingKey,
                                            )}
                                        />
                                    </>
                                ) : (
                                    <>
                                        <div className="tw-flex tw-gap-4 tw-mb-3 tw-border-b tw-border-gray-200">
                                            <button
                                                type="button"
                                                onClick={() => setSignalConfigTab("read")}
                                                className={`tw-px-1 tw-pb-2 tw-text-sm tw-border-b-2 ${signalConfigTab === "read"
                                                    ? "tw-border-blue-500 tw-text-blue-600 tw-font-medium"
                                                    : "tw-border-transparent tw-text-gray-500"
                                                    }`}
                                            >
                                                Read Config
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setSignalConfigTab("write")}
                                                className={`tw-px-1 tw-pb-2 tw-text-sm tw-border-b-2 ${signalConfigTab === "write"
                                                    ? "tw-border-blue-500 tw-text-blue-600 tw-font-medium"
                                                    : "tw-border-transparent tw-text-gray-500"
                                                    }`}
                                            >
                                                Write Config
                                            </button>
                                        </div>

                                        {signalConfigTab === "read" && (
                                            <ProtocolAndConfigFields
                                                endpoint={editSource}
                                                config={editSignalConfig}
                                                onConfigChange={setEditSignalConfig}
                                                physicalTypeByAddress={physicalTypeByAddress}
                                                deviceFlags={deviceFlags}
                                                uartConflictWarning={uartConflictFor(
                                                    editSource,
                                                    editSignalConfig.protocol,
                                                    editingKey,
                                                )}
                                            />
                                        )}

                                        {signalConfigTab === "write" && (
                                            <ProtocolAndConfigFields
                                                endpoint={editSource}
                                                config={
                                                    editSignalWriteEnabled
                                                        ? editSignalWriteConfig
                                                        : editSignalConfig
                                                }
                                                onConfigChange={(cfg) => {
                                                    setEditSignalWriteEnabled(true);
                                                    setEditSignalWriteConfig(cfg);
                                                }}
                                                physicalTypeByAddress={physicalTypeByAddress}
                                                deviceFlags={deviceFlags}
                                                uartConflictWarning={uartConflictFor(
                                                    editSource,
                                                    editSignalWriteEnabled
                                                        ? editSignalWriteConfig.protocol
                                                        : editSignalConfig.protocol,
                                                    editingKey,
                                                )}
                                            />
                                        )}
                                    </>
                                )}

                                <label className="tw-text-xs tw-text-gray-500 tw-block tw-mb-1 tw-mt-2">
                                    Color
                                </label>
                                <input
                                    type="color"
                                    value={`#${editInterpColor}`}
                                    onChange={(e) =>
                                        setEditInterpColor(
                                            e.target.value.replace("#", ""),
                                        )
                                    }
                                    className="tw-w-full tw-h-8 tw-border tw-rounded tw-mb-3"
                                />

                                <div className="tw-flex tw-items-center tw-gap-3 tw-mb-3">
                                    <label className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-600">
                                        <input
                                            type="checkbox"
                                            checked={editInterpInverted}
                                            onChange={(e) =>
                                                setEditInterpInverted(
                                                    e.target.checked,
                                                )
                                            }
                                        />
                                        Inverted
                                    </label>
                                    <label className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-600">
                                        <input
                                            type="checkbox"
                                            checked={editInterpAuxillary}
                                            onChange={(e) =>
                                                setEditInterpAuxillary(
                                                    e.target.checked,
                                                )
                                            }
                                        />
                                        Auxiliary
                                    </label>
                                </div>
                            </>
                        )
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {signalModalMode === "create" ? (
                        <>
                            <button
                                type="button"
                                onClick={() => setSignalModalOpen(false)}
                                className="tw-px-3 tw-py-1 tw-text-xs tw-bg-gray-100 tw-text-gray-600 hover:tw-bg-gray-200 tw-rounded"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddInterpretation}
                                disabled={
                                    !newSignalName.trim() ||
                                    physicalEndpointOptions.includes(
                                        newSignalName.trim(),
                                    ) ||
                                    interpretations.some(
                                        (i) => i.name === newSignalName.trim(),
                                    ) ||
                                    uartConflictFor(
                                        newSignalEndpoint ||
                                        physicalEndpointOptions[0] ||
                                        "",
                                        newSignalConfig.protocol,
                                    ) ||
                                    savingLogicalSignal
                                }
                                className="tw-px-3 tw-py-1 tw-text-xs tw-text-white tw-bg-blue-500 hover:tw-bg-blue-600 tw-rounded disabled:tw-opacity-50"
                            >
                                {savingLogicalSignal ? "Saving..." : "Add Signal"}
                            </button>
                        </>
                    ) : (
                        editingInterp && (
                            <>
                                {canWrite && (
                                    <button
                                        type="button"
                                        onClick={() =>
                                            handleDeleteInterpretation(editingInterp)
                                        }
                                        disabled={savingEdit}
                                        className="tw-px-3 tw-py-1 tw-text-xs tw-text-red-600 hover:tw-bg-red-50 tw-rounded disabled:tw-opacity-50 tw-me-auto"
                                    >
                                        {editingIsResettable
                                            ? "Reset to profile"
                                            : "Delete"}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={handleCancelEdit}
                                    className="tw-px-3 tw-py-1 tw-text-xs tw-bg-gray-100 tw-text-gray-600 hover:tw-bg-gray-200 tw-rounded"
                                >
                                    Cancel
                                </button>
                                {canWrite && (
                                    <button
                                        onClick={() =>
                                            handleSaveEditInterpretation(editingKey)
                                        }
                                        disabled={savingEdit}
                                        className="tw-px-3 tw-py-1 tw-text-xs tw-text-white tw-bg-blue-500 hover:tw-bg-blue-600 tw-rounded disabled:tw-opacity-50"
                                    >
                                        {savingEdit ? "Saving..." : "Save"}
                                    </button>
                                )}
                            </>
                        )
                    )}
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default DeviceEditSidebar;
